import React from 'react';
import clsx from 'clsx';
import Image from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import Slider from 'components/commercetools-ui/atoms/slider';
import useImageSizes from 'helpers/hooks/useImageSizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { mobile, tablet } from 'helpers/utils/screensizes';
import PaginationDots from './pagination-dots';

type Props = {
  images: string[];
  alt: string;
  uniqueFFIDText: string;
  ffid?: string;
  hoverDelayMs?: number;
  classNames?: string;
  url?: string;
  onClick?: () => void;
  productTileIndex?: number;
};

export default function ProductCardImageCarousel(props: Readonly<Props>) {
  const {
    images,
    alt,
    uniqueFFIDText,
    ffid,
    hoverDelayMs = 900,
    classNames = '',
    url,
    onClick,
    productTileIndex,
  } = props;

  const total = images?.length ?? 0;
  const hasCarousel = total > 1;

  const [swiperInstance, setSwiperInstance] = React.useState<any>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const [isDesktopSize] = useMediaQuery(tablet);
  const [isMobileSize] = useMediaQuery(mobile);
  const { isTouchDevice } = useTouchDevice();

  const canHover = isDesktopSize && !isTouchDevice;
  const tileImageSizes = useImageSizes({
    sm: 0.15,
    md: 0.15,
    lg: 0.1,
    defaultSize: 0.1,
    maxWidth: 250,
  });
  React.useEffect(() => {
    if (swiperInstance?.autoplay && hasCarousel) swiperInstance.autoplay.stop();
  }, [swiperInstance, hasCarousel]);

  const startHoverAutoplay = React.useCallback(() => {
    if (!hasCarousel || !canHover) return;
    swiperInstance?.autoplay?.start?.();
  }, [isDesktopSize, canHover, hasCarousel, swiperInstance]);

  const stopHoverAutoplay = React.useCallback(() => {
    if (!hasCarousel || !canHover) return;
    swiperInstance?.autoplay?.stop?.();

    // optional: reset to first frame
    if (typeof swiperInstance?.slideToLoop === 'function') {
      swiperInstance.slideToLoop(0, 200);
    } else if (typeof swiperInstance?.slideTo === 'function') {
      swiperInstance.slideTo(0, 200);
    }
  }, [isDesktopSize, canHover, hasCarousel, swiperInstance]);

  if (!images?.length) return null;

  const hoverHandlers = canHover
    ? {
        onPointerEnter: startHoverAutoplay,
        onPointerLeave: stopHoverAutoplay,
        onMouseEnter: startHoverAutoplay,
        onMouseLeave: stopHoverAutoplay,
      }
    : {};

  const focusHandlers = hasCarousel
    ? {
        onFocus: startHoverAutoplay,
        onBlur: stopHoverAutoplay,
        tabIndex: 0,
      }
    : {};

  const allowTouch = !canHover || isTouchDevice;

  return (
    <Link
      link={url}
      onClick={onClick}
      className={clsx('relative size-full', classNames, '!z-20')}
      {...hoverHandlers}
      {...focusHandlers}
      data-testid="ctr-image-carousel"
      tabIndex={-1}
      aria-hidden="true"
    >
      <Slider
        arrows={false}
        allowTouchMove={allowTouch}
        simulateTouch={allowTouch}
        touchStartPreventDefault={false}
        touchMoveStopPropagation={false}
        slidesPerView={1}
        spaceBetween={0}
        speed={250}
        uniqueFFIDText={uniqueFFIDText}
        onInit={(swiper) => setSwiperInstance(swiper)}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper?.realIndex ?? swiper?.activeIndex ?? 0);
        }}
        loop={hasCarousel}
        autoplay={
          hasCarousel
            ? {
                delay: hoverDelayMs,
                disableOnInteraction: false,
              }
            : false
        }
        a11y={{
          enabled: false,
          prevSlideMessage: 'Previous image',
          nextSlideMessage: 'Next image',
          paginationBulletMessage: 'Go to image {{index}}',
        }}
      >
        {images.map((src, idx) => (
          <div
            key={`${src}-${idx}`}
            className={`relative h-100 w-full bg-white dark:bg-gmsb-header-gray sm:h-184`}
            data-ffid={`ctr-${ffid ? ffid + '-' : ''}-image-box-${idx}`}
          >
            <Image
              src={src}
              fill
              alt={alt}
              style={{ objectFit: 'contain' }}
              loading={Number(productTileIndex) < 4 && idx === 0 ? 'eager' : 'lazy'}
              data-ffid={`img-${ffid ? ffid + '-' : ''}product-image-${idx}`}
              sizes={tileImageSizes}
              fetchPriority={Number(productTileIndex) < 4 && idx === 0 ? 'high' : 'auto'}
              priority={Number(productTileIndex) < 4 && idx === 0}
            />
          </div>
        ))}
      </Slider>
      <div className="pointer-events-none z-10 mt-8 flex justify-center">
        <PaginationDots
          total={total}
          active={activeIndex}
          ffid={uniqueFFIDText}
          dotSize={isMobileSize ? 6 : 4}
          alwaysRender={true}
        />
      </div>
    </Link>
  );
}
